import { CircularProgress, Typography } from "@mui/material";
import { Colors } from "Components/Layout/Themes/Colors";
import { CloseIcon, DoneIcon } from "Components/Shared/Icons";
import { Resources, useResource } from "Translations/Resources";
import type { ReactNode } from "react";

const COMPONENT_RESOURCES =
  Resources.Hunter.Merchants.NewRegistration.Form.StatusIndicator;

type StatusIndicatorProps = {
  company: string | null;
  isInvalid: boolean;
  isLoadingCompany: boolean;
  isCompanyAvailable: boolean;
};

export const StatusIndicator = ({
  company,
  isInvalid,
  isLoadingCompany,
  isCompanyAvailable,
}: StatusIndicatorProps) => {
  const { t } = useResource();

  if (isLoadingCompany) {
    return (
      <Message variant="info">
        <CircularProgress size={16} />
        {t(COMPONENT_RESOURCES.Loading)}
      </Message>
    );
  }

  if (isInvalid) {
    return (
      <Message variant="error">
        <CloseIcon height="16" width="16" />
        {t(COMPONENT_RESOURCES.CompanyNotFound)}
      </Message>
    );
  }

  if (company && isCompanyAvailable) {
    return (
      <Message variant="success">
        <DoneIcon height="16" width="16" />
        {t(COMPONENT_RESOURCES.CompanyAvailable, { company })}
      </Message>
    );
  }

  if (company) {
    return (
      <Message variant="error">
        <CloseIcon height="16" width="16" />
        {t(COMPONENT_RESOURCES.CompanyNotAvailable, { company })}
      </Message>
    );
  }

  return <>{t(COMPONENT_RESOURCES.EnterCompanyNumber)}</>;
};

type MessageVariant = "success" | "error" | "info";

type MessageProps = { children: ReactNode; variant: MessageVariant };

const Message = ({ children, variant }: MessageProps) => {
  const variantToColor: Record<MessageVariant, string> = {
    success: "green",
    error: Colors.Red,
    info: Colors.Black,
  };

  return (
    <Typography
      gap={2}
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      color={variantToColor[variant]}
    >
      {children}
    </Typography>
  );
};
